export interface PageSkeletonProps {
  /** Number of metric card placeholders (default: 4) */
  metricCards?: number;
  /** Number of metric card columns on large screens (default: 4) */
  metricColumns?: 2 | 3 | 4;
  /** Number of content block placeholders below metrics (default: 1) */
  contentBlocks?: number;
  /** Height of each content block in pixels (default: 256) */
  contentBlockHeight?: number;
}

const columnClasses = {
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
} as const;

export function PageSkeleton({
  metricCards = 4,
  metricColumns = 4,
  contentBlocks = 1,
  contentBlockHeight = 256,
}: PageSkeletonProps = {}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: <output> defaults to display:inline and would break this block-level (space-y-6) page skeleton's layout; role="status" already gives the correct ARIA live-region semantics.
    <div className="animate-pulse space-y-6" role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">Loading page…</span>
      {/* Header skeleton */}
      <div className="space-y-2">
        <div className="h-6 w-48 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-72 rounded bg-gray-200 dark:bg-gray-700" />
      </div>

      {/* Metric cards skeleton */}
      {metricCards > 0 && (
        <div className={`grid gap-4 ${columnClasses[metricColumns]}`}>
          {Array.from({ length: metricCards }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder cards (count from the metricCards prop) with no data identity — this list is never reordered or filtered.
            <div key={i} className="h-24 rounded-lg border border-light bg-surface p-4">
              <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="mt-3 h-6 w-16 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          ))}
        </div>
      )}

      {/* Content block skeletons */}
      {Array.from({ length: contentBlocks }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder blocks (count from the contentBlocks prop) with no data identity — this list is never reordered or filtered.
        <div key={i} className="rounded-lg border border-light bg-surface" style={{ height: contentBlockHeight }} />
      ))}
    </div>
  );
}
